export const ACTIONS = {
  // Loading states
  FETCH_START: "FETCH_START",
  FETCH_SUCCESS: "FETCH_SUCCESS",
  FETCH_ERROR: "FETCH_ERROR",

  // Data operations
  SET_ITEMS: "SET_ITEMS",
  SET_LOADING: "SET_LOADING",

  // Pagination
  SET_PAGE: "SET_PAGE",
  SET_PAGE_FROM_ANCHOR: "SET_PAGE_FROM_ANCHOR",
  CLEAR_ANCHOR_FLAG: "CLEAR_ANCHOR_FLAG",

  // Reset
  RESET: "RESET",
};

export const initialPaginatedState = {
  // Data
  items: [],

  // Loading & Error
  loading: false,
  // Direction of the in-flight fetch ('next' | 'prev'). Null when not
  // loading. Lets bidirectional consumers render a header/footer
  // skeleton on the correct edge.
  loadingDirection: null,
  error: null,

  // Pagination
  page: 1,
  nextPage: null,
  prevPage: null,

  // Anchor handling
  hasLoadedAnchor: false,
  isSettingPageFromAnchor: false,
};

const mergeItems = (existingItems, newItems, direction, maxItems) => {
  const safeNewItems = newItems || [];

  if (direction === "prev") {
    const combined = [...safeNewItems, ...existingItems];
    return combined.length > maxItems ? combined.slice(0, maxItems) : combined;
  }

  const combined = [...existingItems, ...safeNewItems];
  return combined.length > maxItems ? combined.slice(-maxItems) : combined;
};

export const paginatedDataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        loadingDirection: action.direction || "next",
        error: null,
        ...(action.reset && {
          items: [],
          page: 1,
          nextPage: null,
          prevPage: null,
          hasLoadedAnchor: false,
          isSettingPageFromAnchor: false,
        }),
      };

    case ACTIONS.FETCH_SUCCESS: {
      const {
        items: newItems,
        pagination,
        maxItems,
        reset,
        direction,
      } = action.payload;

      let resultItems;
      if (reset) {
        resultItems = newItems || [];
      } else {
        resultItems = mergeItems(state.items, newItems, direction, maxItems);
      }

      return {
        ...state,
        loading: false,
        loadingDirection: null,
        items: resultItems,
        nextPage: pagination?.nextPage ?? null,
        prevPage: pagination?.prevPage ?? null,
        hasLoadedAnchor: action.anchorLoaded ? true : state.hasLoadedAnchor,
      };
    }

    case ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        loadingDirection: null,
        error: action.payload,
      };

    case ACTIONS.SET_ITEMS:
      return {
        ...state,
        items: action.payload,
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        loadingDirection: action.payload ? state.loadingDirection : null,
      };

    case ACTIONS.SET_PAGE:
      return {
        ...state,
        page: action.payload,
      };

    case ACTIONS.SET_PAGE_FROM_ANCHOR:
      return {
        ...state,
        page: action.payload,
        isSettingPageFromAnchor: true,
      };

    case ACTIONS.CLEAR_ANCHOR_FLAG:
      return {
        ...state,
        isSettingPageFromAnchor: false,
      };

    case ACTIONS.RESET:
      return {
        ...initialPaginatedState,
        // Preserve any values passed in action
        ...action.payload,
      };

    default:
      throw new Error(
        `usePaginatedData: Unhandled action type: ${action.type}`,
      );
  }
};
